import { useEffect } from 'react';

import type { UseListFocusManagerState } from '../../../components/lists/useListFocusManager';

type UseFormulaDropdownKeyboardControlsArgs = {
    enabled: boolean;
    focusedFormula?: string;
    listFocusManager: UseListFocusManagerState;
    selectFormula: (formula: string) => void;
};

/**
 * Captures formula dropdown navigation before Handsontable/editor shortcuts handle the same keys.
 */
const useFormulaDropdownKeyboardControls = ({
    enabled,
    focusedFormula,
    listFocusManager,
    selectFormula,
}: UseFormulaDropdownKeyboardControlsArgs) => {
    const { moveListFocusDown, moveListFocusUp } = listFocusManager;

    useEffect(() => {
        if (!enabled) return;

        const onKeyDownCapture = (event: KeyboardEvent) => {
            if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                moveListFocusDown();
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                moveListFocusUp();
                return;
            }

            if (focusedFormula && (event.key === 'Enter' || event.key === 'Tab')) {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                selectFormula(focusedFormula);
            }
        };

        document.addEventListener('keydown', onKeyDownCapture, true);

        return () => {
            document.removeEventListener('keydown', onKeyDownCapture, true);
        };
    }, [enabled, focusedFormula, moveListFocusDown, moveListFocusUp, selectFormula]);
};

export default useFormulaDropdownKeyboardControls;
